import { useQuery } from '@tanstack/react-query';
import type { ProSpaceCatalogEntry } from '@erp/data-access/interfaces';
import { useDataService, useStorageService } from '@/lib/service-context';

export const RESOURCES_BUCKET = 'resources';

export interface ProCatalogEntry {
  id: string;
  name: string;
  sizeBytes: number;
  /** Sous-dossier d'origine, ou null si posé à la racine de « ProSpace ». */
  folderName: string | null;
  /** URL publique du binaire — le bucket `resources` est public. */
  url: string;
  isPdf: boolean;
}

function isPdf(entry: ProSpaceCatalogEntry): boolean {
  return (
    entry.mimeType === 'application/pdf' ||
    entry.fileName.toLowerCase().endsWith('.pdf')
  );
}

/**
 * Le catalogue partagé : le contenu du dossier « ProSpace » du tenant.
 *
 * Passe par `ds.resources.listProSpaceCatalog()` → RPC `pro_shared_catalog()`,
 * et NON par une lecture de `resource_files`. Les policies RLS s'additionnent :
 * un compte à la fois membre d'un tenant (dashboard) et pro d'un autre verrait
 * sinon la bibliothèque interne de son propre tenant remonter ici — donc sous
 * les yeux du client en mode client. La RPC, elle, ne répond que le périmètre
 * publié du tenant du pro.
 */
export function useProCatalog() {
  const ds = useDataService();
  const storage = useStorageService();

  return useQuery({
    queryKey: ['pro-catalog'],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<ProCatalogEntry[]> => {
      const rows = await ds.resources.listProSpaceCatalog();
      return rows.map((row) => ({
        id: row.fileId,
        name: row.fileName,
        sizeBytes: row.sizeBytes,
        folderName: row.inRoot ? null : row.folderName,
        url: storage.getPublicUrl(RESOURCES_BUCKET, row.storagePath),
        isPdf: isPdf(row),
      }));
    },
  });
}
